import { AlertCircle, CheckCircle2, Mail, Phone } from "lucide-react";
import type { ReactNode } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type { PortalBranding } from "@/features/portal/types";

type Variant = "invalid" | "submitted" | "thankyou";

interface StatusLandingProps {
  variant: Variant;
  branding?: PortalBranding | null;
  title?: string;
  message?: string;
}

const COPY: Record<Variant, { icon: ReactNode; title: string; message: string }> = {
  invalid: {
    icon: <AlertCircle className="text-destructive size-8" />,
    title: "This link is invalid or has expired",
    message:
      "The onboarding link you followed is no longer valid. Please contact your tax professional to request a new one.",
  },
  submitted: {
    icon: <CheckCircle2 className="text-primary size-8" />,
    title: "You've already submitted this form",
    message:
      "This profile has already been completed. If you need to make changes, please contact your tax professional.",
  },
  thankyou: {
    icon: <CheckCircle2 className="text-primary size-8" />,
    title: "Thank you — your profile was submitted",
    message:
      "Your information has been sent to your tax professional. They'll review it and reach out if anything else is needed.",
  },
};

/** Shared landing card for the three onboarding non-wizard states (§3.A). */
export function OnboardingStatusLanding({ variant, branding, title, message }: StatusLandingProps) {
  const copy = COPY[variant];
  return (
    <div className="flex min-h-[60vh] w-full items-center justify-center p-4">
      <Card className="w-full max-w-md text-center">
        <CardHeader className="items-center gap-3">
          <div className="flex justify-center">{copy.icon}</div>
          {branding?.firmName && (
            <p className="text-muted-foreground text-sm font-medium">{branding.firmName}</p>
          )}
          <CardTitle className="text-xl">{title ?? copy.title}</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4">
          <p className="text-muted-foreground text-sm">{message ?? copy.message}</p>
          {(branding?.phone || branding?.email) && (
            <div className="text-muted-foreground grid gap-2 border-t pt-4 text-sm">
              {branding?.phone && (
                <p className="flex items-center justify-center gap-2">
                  <Phone className="size-4" />
                  {branding.phone}
                </p>
              )}
              {branding?.email && (
                <p className="flex items-center justify-center gap-2">
                  <Mail className="size-4" />
                  {branding.email}
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
